import React from 'react';

const Contributing = () => {
  return (
    <div className="doc-content">
      <div className="quickstart-header">
        <p className="quickstart-category">Community</p>
        <h1>Contributing</h1>
      </div>
      
      <div className="info-banner">
        <i className="fa-solid fa-info-circle"></i>
        <div>
          <p>Join our open-source community! Your contributions drive MemU's innovation forward and help build the future of AI memory.</p>
        </div>
      </div>
      
      <p>We build trust through <strong>open-source collaboration</strong>. Your creative contributions drive MemU's innovation forward.</p>
      
      <p>Explore our GitHub issues and projects to get started and make your mark on the <strong>future of MemU</strong>.</p>
      
      <div className="platform-cards">
        <div className="platform-card">
          <div className="card-icon">
            <i className="fa-solid fa-code"></i>
          </div>
          <h3>Code Contributions</h3>
          <p>Contribute to core features, bug fixes, and performance improvements through our GitHub repository.</p>
        </div>
        
        <div className="platform-card">
          <div className="card-icon">
            <i className="fa-solid fa-book"></i>
          </div>
          <h3>Documentation</h3>
          <p>Help improve documentation, write tutorials, and create guides for the community.</p>
        </div>
      </div>
      
      <div className="platform-section">
        <h2>How to Contribute</h2>
        
        <h3>Getting Started</h3>
        <p>Contributing to MemU is easy! Follow these steps to get started:</p>
        
        <ol>
          <li><strong>Fork the Repository:</strong> Click the "Fork" button on our <a href="https://github.com/NevaMind-AI/memU" target="_blank" rel="noopener noreferrer">GitHub repository</a></li>
          <li><strong>Clone Your Fork:</strong> <code>git clone https://github.com/YOUR_USERNAME/memu.git</code></li>
          <li><strong>Create a Branch:</strong> <code>git checkout -b feature/your-feature-name</code></li>
          <li><strong>Make Changes:</strong> Implement your feature or fix</li>
          <li><strong>Test:</strong> Run tests to ensure everything works</li>
          <li><strong>Submit a Pull Request:</strong> Open a PR with a clear description</li>
        </ol>
        
        <h3>Development Setup</h3>
        
        <div className="code-block">
          <pre><code>{`# Clone the repository
git clone https://github.com/NevaMind-AI/memU.git
cd memu

# Create virtual environment
python -m venv venv
source venv/bin/activate  # On Windows: venv\\Scripts\\activate

# Install dependencies
pip install -e ".[dev]"

# Install pre-commit hooks
pre-commit install

# Run tests
pytest`}</code></pre>
        </div>
        
        <h3>Contribution Types</h3>
        
        <div className="platform-cards">
          <div className="platform-card">
            <div className="card-icon">
              <i className="fa-solid fa-bug"></i>
            </div>
            <h3>Bug Reports</h3>
            <p>Found a bug? Help us fix it by providing detailed reproduction steps and system information.</p>
            <a href="https://github.com/NevaMind-AI/memU/issues/new?template=bug_report.md" className="getting-started-link" target="_blank" rel="noopener noreferrer">Report Bug →</a>
          </div>
          
          <div className="platform-card">
            <div className="card-icon">
              <i className="fa-solid fa-lightbulb"></i>
            </div>
            <h3>Feature Requests</h3>
            <p>Have an idea for a new feature? Share it with the community and discuss implementation approaches.</p>
            <a href="https://github.com/NevaMind-AI/memU/issues/new?template=feature_request.md" className="getting-started-link" target="_blank" rel="noopener noreferrer">Request Feature →</a>
          </div>
          
          <div className="platform-card">
            <div className="card-icon">
              <i className="fa-solid fa-code-pull-request"></i>
            </div>
            <h3>Pull Requests</h3>
            <p>Ready to contribute code? Check our open issues for "good first issue" or "help wanted" labels.</p>
            <a href="https://github.com/NevaMind-AI/memU/pulls" className="getting-started-link" target="_blank" rel="noopener noreferrer">View PRs →</a>
          </div>
        </div>
        
        <h3>Code Guidelines</h3>
        
        <h4>Code Style</h4>
        <ul>
          <li><strong>Python:</strong> Follow PEP 8 guidelines</li>
          <li><strong>Type Hints:</strong> Use type hints for all function signatures</li>
          <li><strong>Docstrings:</strong> Document all public functions and classes</li>
          <li><strong>Formatting:</strong> Use Black for code formatting</li>
          <li><strong>Linting:</strong> Code must pass flake8 and mypy checks</li>
        </ul>
        
        <h4>Testing</h4>
        <ul>
          <li><strong>Unit Tests:</strong> Write tests for all new functionality</li>
          <li><strong>Integration Tests:</strong> Test complex workflows end-to-end</li>
          <li><strong>Coverage:</strong> Maintain at least 80% test coverage</li>
          <li><strong>Performance Tests:</strong> Include benchmarks for performance-critical code</li>
        </ul>
        
        <h3>Pull Request Process</h3>
        
        <div className="info-banner" style={{backgroundColor: '#f0f9ff', borderLeft: '4px solid #3b82f6'}}>
          <i className="fa-solid fa-info-circle" style={{color: '#3b82f6'}}></i>
          <div>
            <p><strong>Before submitting:</strong> Make sure your code passes all tests, follows our style guidelines, and includes appropriate documentation.</p>
          </div>
        </div>
        
        <h4>PR Checklist</h4>
        <ul>
          <li>Code follows our style guidelines</li>
          <li>Self-review completed</li>
          <li>Tests added for new functionality</li>
          <li>All tests pass locally</li>
          <li>Documentation updated if needed</li>
          <li>PR description clearly explains changes</li>
          <li>Linked to relevant issues</li>
        </ul>
        
        <h3>Community Guidelines</h3>
        
        <h4>Code of Conduct</h4>
        <p>We are committed to providing a welcoming and inclusive experience for everyone. Please read our <a href="https://github.com/nevamind/memu/blob/main/CODE_OF_CONDUCT.md" target="_blank" rel="noopener noreferrer">Code of Conduct</a>.</p>
        
        <h4>Communication</h4>
        <ul>
          <li><strong>Be respectful:</strong> Treat all community members with respect</li>
          <li><strong>Be constructive:</strong> Provide helpful feedback and suggestions</li>
          <li><strong>Be patient:</strong> Remember that contributors volunteer their time</li>
          <li><strong>Ask questions:</strong> Don't hesitate to ask for help or clarification</li>
        </ul>
        
        <h3>Recognition</h3>
        
        <p>We value all contributions to MemU! Contributors are recognized in several ways:</p>
        
        <ul>
          <li><strong>Contributors List:</strong> All contributors are listed in our README</li>
          <li><strong>Release Notes:</strong> Significant contributions are highlighted in release notes</li>
          <li><strong>Special Recognition:</strong> Outstanding contributions may receive special recognition</li>
          <li><strong>Swag:</strong> Active contributors may receive MemU swag and stickers</li>
        </ul>
      </div>
    </div>
  );
};

export default Contributing;
